/**
 * 플레어 스택 복사열·높이 계산 — API 521(7판, 2020.06) Annex F.2 "단순법"
 * (5.5.5·F.2, Annex C.2.2 예제와 같은 절차).
 *
 * ⚠ 이 방법 자체가 표준에서 "예비 스크리닝(preliminary screening)"용으로
 *   명시한 근사법이다 — 화염을 점원(point source) 하나로 본다(F.2:
 *   "The methods presented here assume that a flame can be modeled by a
 *   single point source for radiation"). 정밀 설계에는 플레어 제조사 검증이
 *   필요하다고 표준이 직접 권고한다.
 *
 * 절차(Annex C.2.2, Figure C.2 기준):
 *   1) 발열량 Q = 질량유량 × 저위발열량 (또는 직접 입력).
 *   2) 화염길이 L — Figure F.1/F.2(=Figure 7) "화염길이 vs 발열량" 그래프의
 *      커브핏 L=coefficient×Q[kW]^exponent (`src/data/flareRadiation.ts`
 *      참고, 출처·검증 근거 명시).
 *   3) 플레어 팁 유출속도 uj — 팁 조건(T,P,M,Z)에서의 체적유량 ÷ 팁 단면적.
 *   4) 바람에 의한 화염 굴곡 Δx,Δy — Figure F.3(=Figure 9), X=u∞/uj 에서
 *      Δx/L·Δy/L 을 유리함수 커브핏으로 구한다.
 *   5) 점원 복사식 (F.1): K = τ·F·Q/(4π·D²) → 목표 K 에서 D(화염중심~
 *      대상지점 거리) 역산.
 *   6) Figure C.2 기하: r'=r−0.5Δx, h'=√(D²−r'²), h=h'−0.5Δy.
 *
 * 검증: `data/verify-api521-flare-radiation.py` 가 Annex C.2.2 예제
 * (Ma=0.2 → h=33.3 m, Ma=0.5 → h=25.9 m) 전체를 재현해 10 % 이내로 대조.
 */
import {
  FLAME_LENGTH_CORRELATION,
  WIND_TILT_CORRELATION,
  F_FACTOR_TABLE,
  type FFactorGas,
  type RationalCurveCoefficients,
} from "../data/flareRadiation";

const UNIVERSAL_MOLAR_VOLUME_M3_PER_KMOL = 22.414; // 0°C, 101.325 kPa
const STANDARD_T_K = 273.15;
const STANDARD_P_KPA = 101.325;

/** 화염길이(m) — 발열량 Q(kW)에서 Figure 7(=Figure F.1/F.2) 커브핏으로 구한다. */
export function flameLengthM(heatReleaseKw: number): { readonly lengthM: number; readonly extrapolated: boolean } {
  const { coefficient, exponent, validQMinKw, validQMaxKw } = FLAME_LENGTH_CORRELATION;
  const lengthM = coefficient * Math.pow(heatReleaseKw, exponent);
  const extrapolated = heatReleaseKw < validQMinKw || heatReleaseKw > validQMaxKw;
  return { lengthM, extrapolated };
}

function evalRational(c: RationalCurveCoefficients, x: number): number {
  return (c.a0 + c.a1 * x) / (1 + c.b1 * x + c.b2 * x * x);
}

/**
 * Figure F.3(=Figure 9) — X=u∞/uj 에서 Δx/L, Δy/L 을 유리함수 커브핏으로
 * 구한다. X=0(무풍)에서 Δx/L→0, Δy/L→1(화염이 그대로 수직)이 되는 경계조건이
 * 물리적으로 맞는 것까지 확인했다.
 */
export function windTiltRatios(velocityRatio: number): {
  readonly dxOverL: number;
  readonly dyOverL: number;
  readonly clamped: boolean;
} {
  const { dxOverL, dyOverL, validXMax } = WIND_TILT_CORRELATION;
  const x = Math.max(0, velocityRatio);
  const clamped = velocityRatio < 0 || velocityRatio > validXMax;
  return {
    dxOverL: evalRational(dxOverL, x),
    dyOverL: evalRational(dyOverL, x),
    clamped,
  };
}

/**
 * Table F.1 — 가스 종류·버너(팁) 직경에서 F-factor(복사열 분율)를
 * 선형보간으로 조회한다. 표 범위를 벗어나면 양 끝값으로 고정(clamp)한다.
 */
export function lookupFFactor(
  gas: FFactorGas,
  burnerDiameterCm: number,
): { readonly fFactor: number; readonly clamped: boolean } {
  const pts = F_FACTOR_TABLE[gas];
  const dMin = pts[0]!.burnerDiameterCm;
  const dMax = pts[pts.length - 1]!.burnerDiameterCm;
  const clamped = burnerDiameterCm < dMin || burnerDiameterCm > dMax;
  const d = Math.max(dMin, Math.min(dMax, burnerDiameterCm));

  for (let i = 0; i < pts.length - 1; i++) {
    const a = pts[i]!;
    const b = pts[i + 1]!;
    if (d >= a.burnerDiameterCm && d <= b.burnerDiameterCm) {
      const frac =
        b.burnerDiameterCm === a.burnerDiameterCm
          ? 0
          : (d - a.burnerDiameterCm) / (b.burnerDiameterCm - a.burnerDiameterCm);
      return {
        fFactor: a.fractionHeatRadiated + frac * (b.fractionHeatRadiated - a.fractionHeatRadiated),
        clamped,
      };
    }
  }
  const last = pts[pts.length - 1]!;
  return { fFactor: last.fractionHeatRadiated, clamped };
}

export interface FlareHeightInput {
  readonly massFlowKgH: number;
  readonly molecularWeight: number;
  readonly temperatureK: number;
  readonly compressibility: number;
  readonly tipPressureKpaAbs: number;
  readonly tipInsideDiameterM: number;
  readonly windVelocityMs: number;
  readonly heatReleaseMode: "direct" | "fromMassFlow";
  /** heatReleaseMode "direct" 일 때만 사용 (kW). */
  readonly heatReleaseKw: number;
  /** heatReleaseMode "fromMassFlow" 일 때만 사용 (kJ/kg, 저위발열량). */
  readonly lowerHeatingValueKjKg: number;
  /** F — 복사열 분율(0~1). lookupFFactor() 로 구하거나 직접 입력. */
  readonly fractionHeatRadiated: number;
  /** τ — 대기 투과율. 모르면 1.0(API 521 예제 기본 가정). */
  readonly transmissivity: number;
  /** K — 목표 허용 방사강도 (kW/m²). */
  readonly allowableRadiationKwM2: number;
  /** r — 플레어 스택에서 대상 지점까지의 지면 수평거리 (m). */
  readonly gradeDistanceM: number;
}

export interface FlareHeightSuccess {
  readonly ok: true;
  readonly heatReleaseKw: number;
  readonly flameLengthM: number;
  readonly vaporVolumeFlowM3S: number;
  readonly tipVelocityMs: number;
  readonly windToTipVelocityRatio: number;
  readonly horizontalDisplacementM: number;
  readonly verticalDisplacementM: number;
  readonly radiationDistanceM: number;
  readonly requiredHeightM: number;
  readonly warnings: readonly string[];
}

export interface FlareHeightFailure {
  readonly ok: false;
  readonly reason: "invalid-input";
  readonly message: string;
}

export type FlareHeightResult = FlareHeightSuccess | FlareHeightFailure;

function invalid(message: string): FlareHeightFailure {
  return { ok: false, reason: "invalid-input", message };
}

export function requiredFlareHeight(input: FlareHeightInput): FlareHeightResult {
  if (!Number.isFinite(input.massFlowKgH) || input.massFlowKgH <= 0) {
    return invalid("질량유량은 0보다 커야 합니다.");
  }
  if (!Number.isFinite(input.molecularWeight) || input.molecularWeight <= 0) {
    return invalid("분자량은 0보다 커야 합니다.");
  }
  if (!Number.isFinite(input.temperatureK) || input.temperatureK <= 0) {
    return invalid("온도는 0 K보다 커야 합니다.");
  }
  if (!Number.isFinite(input.tipPressureKpaAbs) || input.tipPressureKpaAbs <= 0) {
    return invalid("팁 압력은 0보다 커야 합니다.");
  }
  if (!Number.isFinite(input.tipInsideDiameterM) || input.tipInsideDiameterM <= 0) {
    return invalid("팁 내경은 0보다 커야 합니다.");
  }
  if (!Number.isFinite(input.windVelocityMs) || input.windVelocityMs <= 0) {
    return invalid("풍속은 0보다 커야 합니다.");
  }
  if (
    !Number.isFinite(input.fractionHeatRadiated) ||
    input.fractionHeatRadiated <= 0 ||
    input.fractionHeatRadiated >= 1
  ) {
    return invalid("복사열 분율 F 는 0~1 사이여야 합니다.");
  }
  if (!Number.isFinite(input.transmissivity) || input.transmissivity <= 0 || input.transmissivity > 1) {
    return invalid("대기 투과율 τ 는 0보다 크고 1 이하여야 합니다.");
  }
  if (!Number.isFinite(input.allowableRadiationKwM2) || input.allowableRadiationKwM2 <= 0) {
    return invalid("허용 방사강도 K 는 0보다 커야 합니다.");
  }
  if (!Number.isFinite(input.gradeDistanceM) || input.gradeDistanceM <= 0) {
    return invalid("대상 지점까지의 수평거리 r 는 0보다 커야 합니다.");
  }

  let heatReleaseKw: number;
  if (input.heatReleaseMode === "direct") {
    if (!Number.isFinite(input.heatReleaseKw) || input.heatReleaseKw <= 0) {
      return invalid("발열량 Q 는 0보다 커야 합니다.");
    }
    heatReleaseKw = input.heatReleaseKw;
  } else {
    if (!Number.isFinite(input.lowerHeatingValueKjKg) || input.lowerHeatingValueKjKg <= 0) {
      return invalid("저위발열량(LHV)은 0보다 커야 합니다.");
    }
    heatReleaseKw = (input.massFlowKgH * input.lowerHeatingValueKjKg) / 3600;
  }

  const warnings: string[] = [];

  const { lengthM: flameLength, extrapolated: lengthExtrapolated } = flameLengthM(heatReleaseKw);
  if (lengthExtrapolated) {
    warnings.push(
      "발열량이 Figure 7(=Figure F.1/F.2) 그래프의 통상 범위(약 1×10⁴~1.3×10⁷ kW)를 벗어나 화염길이를 외삽했습니다 — 결과를 신중히 검토하십시오.",
    );
  }

  const vaporVolumeFlowM3S =
    ((input.massFlowKgH / input.molecularWeight) *
      UNIVERSAL_MOLAR_VOLUME_M3_PER_KMOL *
      (input.temperatureK / STANDARD_T_K) *
      (STANDARD_P_KPA / input.tipPressureKpaAbs) *
      input.compressibility) /
    3600;
  const tipArea = (Math.PI * input.tipInsideDiameterM ** 2) / 4;
  const tipVelocityMs = vaporVolumeFlowM3S / tipArea;
  if (tipVelocityMs <= 0) {
    return invalid("계산된 팁 유출속도가 0 이하입니다 — 입력값을 확인하십시오.");
  }

  const windToTipVelocityRatio = input.windVelocityMs / tipVelocityMs;
  const { dxOverL, dyOverL, clamped: tiltClamped } = windTiltRatios(windToTipVelocityRatio);
  if (tiltClamped) {
    warnings.push(
      `풍속/팁유속 비(${windToTipVelocityRatio.toFixed(3)})가 Figure 9(=Figure F.3) 그래프의 통상 범위(0~1.2)를 벗어나 커브핏 공식으로 외삽했습니다 — 결과를 신중히 검토하십시오.`,
    );
  }
  const horizontalDisplacementM = dxOverL * flameLength;
  const verticalDisplacementM = dyOverL * flameLength;

  const radiationDistanceM = Math.sqrt(
    (input.transmissivity * input.fractionHeatRadiated * heatReleaseKw) /
      (4 * Math.PI * input.allowableRadiationKwM2),
  );

  const rPrime = input.gradeDistanceM - 0.5 * horizontalDisplacementM;
  const hPrimeSq = radiationDistanceM ** 2 - rPrime ** 2;

  let requiredHeightM: number;
  if (hPrimeSq < 0) {
    warnings.push(
      "지정한 수평거리만으로 이미 허용 방사강도 이내입니다 — 이 기준으로는 추가 높이가 필요하지 않습니다(0 m로 처리).",
    );
    requiredHeightM = 0;
  } else {
    const hPrime = Math.sqrt(hPrimeSq);
    requiredHeightM = Math.max(0, hPrime - 0.5 * verticalDisplacementM);
  }

  return {
    ok: true,
    heatReleaseKw,
    flameLengthM: flameLength,
    vaporVolumeFlowM3S,
    tipVelocityMs,
    windToTipVelocityRatio,
    horizontalDisplacementM,
    verticalDisplacementM,
    radiationDistanceM,
    requiredHeightM,
    warnings,
  };
}
